import { ArrowDown, ArrowRight, Check, Mail, Sparkles } from 'lucide-react'
import { useRef } from 'react'
import { CampaignImpactMedia } from '../components/media/LuminaPortalVideo'
import { useRevealMotion } from '../motion/useRevealMotion'
import '../styles/proposal-intro.css'

const proofWork = [
  {
    title: 'Pie World Gladstone',
    discipline: 'Ordering experience',
    image: '/proof/pie-world-ordering.png',
    alt: 'Pie World Gladstone ordering experience with a cinematic pie hero',
    shape: 'wide',
  },
  {
    title: 'CQ Muddies',
    discipline: 'Local commerce',
    image: '/proof/cq-muddies-commerce.png',
    alt: 'CQ Muddies commerce concept for reserving local mud crabs',
    shape: 'wide',
  },
  {
    title: "Hughesys Que",
    discipline: 'Delivery operations',
    image: '/proof/hughesys-delivery.png',
    alt: 'Hughesys Que live customer delivery locator interface',
    shape: 'tall',
  },
  {
    title: 'Service business system',
    discipline: 'Conversion architecture',
    image: '/proof/service-website.png',
    alt: 'Detailed service business website and quote interface',
    shape: 'wide',
  },
] as const

export function ProposalIntroPage() {
  const appRef = useRef<HTMLDivElement>(null)
  useRevealMotion(appRef)

  return (
    <div className="proposal-page" ref={appRef}>
      <header className="proposal-header">
        <a className="proposal-header__studio" href="#top">
          <span>Penny Wise I.T</span>
          <small>Digital product studio · Central Queensland</small>
        </a>
        <span className="proposal-header__collab">×</span>
        <img
          className="proposal-header__brand"
          src="/brand/sub-zero-logo-primary.png"
          alt="Sub Zero Treats"
        />
        <nav aria-label="Proposal navigation">
          <a href="#selected-work">Selected work</a>
          <a href="/direct-delivery-case">The business case</a>
          <a className="proposal-header__enter" href="/experience">
            Enter the MVP
            <ArrowRight aria-hidden="true" />
          </a>
        </nav>
      </header>

      <main id="top">
        <section className="proposal-hero">
          <div className="proposal-hero__copy reveal">
            <p className="proposal-kicker">Prepared for Sub Zero Treats</p>
            <h1>
              <span>Your next chapter,</span>
              <span>built around what</span>
              <em>you&apos;ve grown.</em>
            </h1>
            <p className="proposal-hero__summary">
              A private, clickable proposal by Steve at Penny Wise I.T: direct
              ordering, personalised buckets and scheduled home delivery,
              designed to retain more repeat-order value without losing the joy
              that makes Sub Zero unmistakable.
            </p>
            <div className="proposal-hero__actions">
              <a href="/experience">
                Explore direct ordering
                <ArrowRight aria-hidden="true" />
              </a>
              <a href="/direct-delivery-case">
                Inspect the delivery case
                <ArrowDown aria-hidden="true" />
              </a>
            </div>
            <div className="proposal-hero__credentials">
              <span>
                <Check aria-hidden="true" />
                Fully clickable
              </span>
              <span>
                <Check aria-hidden="true" />
                Built around the real brand
              </span>
              <span>
                <Check aria-hidden="true" />
                No live order or payment
              </span>
            </div>
          </div>

          <div className="proposal-hero__art" aria-hidden="true">
            <CampaignImpactMedia />
            <div className="proposal-hero__logo-plate">
              <img src="/brand/sub-zero-logo-primary.png" alt="" />
            </div>
            <span className="proposal-hero__edition">Proposal 01 · 2026</span>
            <span className="proposal-hero__spark">
              <Sparkles />
            </span>
          </div>
        </section>

        <section className="proposal-positioning">
          <p className="proposal-kicker reveal">The thinking behind it</p>
          <div className="reveal">
            <h2>
              The platforms are useful.
              <br />
              Dependence is expensive.
            </h2>
            <p>
              This proposal does not ask Sub Zero to abandon DoorDash or Uber
              Eats. It gives repeat customers a memorable reason to order
              direct, then tests whether scheduled local runs can work on real
              numbers.
            </p>
          </div>
          <figure className="proposal-positioning__impact reveal" aria-hidden="true">
            <picture>
              <source
                media="(max-width: 620px)"
                type="image/avif"
                srcSet="/media/higgsfield/editorial/candy-impact-mobile-900x1125.avif"
              />
              <source
                media="(max-width: 620px)"
                type="image/webp"
                srcSet="/media/higgsfield/editorial/candy-impact-mobile-900x1125.webp"
              />
              <source
                type="image/avif"
                srcSet="/media/higgsfield/editorial/candy-impact-desktop-1600x900.avif"
              />
              <img
                src="/media/higgsfield/editorial/candy-impact-desktop-1600x900.webp"
                alt=""
                loading="lazy"
                decoding="async"
              />
            </picture>
          </figure>
          <div className="proposal-positioning__rail reveal">
            <article>
              <span>01</span>
              <strong>Win attention</strong>
              <p>A campaign-quality storefront with movement, colour and depth.</p>
            </article>
            <article>
              <span>02</span>
              <strong>Make it personal</strong>
              <p>Zero turns tastes, texture and adventure into a transparent Bucket.</p>
            </article>
            <article>
              <span>03</span>
              <strong>Test the route</strong>
              <p>Four weeks, visible assumptions and a proceed-adjust-stop decision.</p>
            </article>
          </div>
        </section>

        <section className="proposal-owner-story">
          <img
            className="proposal-owner-story__bucket-logo"
            src="/brand/sub-zero-logo-primary.png"
            alt=""
            aria-hidden="true"
          />
          <div className="proposal-owner-story__eyebrow reveal">
            <span>For everyone who took it from a home idea to a growing community</span>
            <Sparkles aria-hidden="true" />
          </div>
          <div className="proposal-owner-story__statement reveal">
            <p className="proposal-kicker">The part that matters most</p>
            <h2>
              This is not just a store.
              <br />
              It is the <em>next chapter</em> of something built by hand.
            </h2>
          </div>
          <div className="proposal-owner-story__copy reveal">
            <p>
              Sub Zero&apos;s own story begins with food preservation and a
              little fun, then grows into a Capricornia treat brand, a thriving
              Facebook community and a network of more than 70 stockists.
            </p>
            <p>
              That is why the direct channel matters. The customer relationship,
              the ritual and the next chapter should feel unmistakably Sub Zero
              &mdash; not like rented space inside somebody else&apos;s app.
            </p>
            <blockquote>
              <span>Our design principle</span>
              <strong>Protect what made it theirs while making it ready to grow.</strong>
            </blockquote>
            <div className="proposal-owner-story__sources">
              <a
                href="https://subzerotreats.com.au/pages/about-us"
                target="_blank"
                rel="noreferrer"
              >
                Sub Zero&apos;s story
              </a>
              <a
                href="https://www.facebook.com/subzer0treats/"
                target="_blank"
                rel="noreferrer"
              >
                The Facebook community
              </a>
            </div>
          </div>
        </section>

        <section className="proposal-proof" id="selected-work">
          <div className="proposal-proof__heading reveal">
            <p className="proposal-kicker">Selected Penny Wise product work</p>
            <h2>We do not just make pages. We build the thing behind them.</h2>
            <p>
              A sample of interfaces already built across local food, seafood,
              delivery operations and service businesses.
            </p>
          </div>

          <div className="proposal-proof__gallery">
            {proofWork.map((work, index) => (
              <article
                className={`proposal-work proposal-work--${work.shape} reveal`}
                key={work.title}
              >
                <div className="proposal-work__frame">
                  <img src={work.image} alt={work.alt} loading="lazy" />
                </div>
                <div className="proposal-work__meta">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{work.title}</h3>
                  <p>{work.discipline}</p>
                </div>
              </article>
            ))}
          </div>

          <p className="proposal-proof__note reveal">
            Selected interface examples are shown as capability evidence. No
            third-party revenue or performance result is claimed here.
          </p>
        </section>

        <section className="proposal-studio">
          <div className="proposal-studio__copy reveal">
            <p className="proposal-kicker">Penny Wise I.T</p>
            <h2>Local enough to understand it. Technical enough to ship it.</h2>
          </div>
          <div className="proposal-studio__bio reveal">
            <p>
              Steve works with local operators to turn the messy middle —
              ordering, delivery, customer communication and back-office
              workflow — into something customers actually want to use.
            </p>
            <p>
              This prototype applies that same practical product thinking to
              Sub Zero Treats: one memorable media moment, then a calm,
              conversion-focused experience that is simple to use and own.
            </p>
            <div>
              <span>Brand-led UI</span>
              <span>Ordering systems</span>
              <span>Delivery workflows</span>
              <span>Automation</span>
            </div>
          </div>
        </section>

        <section
          className="proposal-included"
          aria-labelledby="proposal-included-title"
        >
          <div className="proposal-included__texture" aria-hidden="true" />
          <div className="proposal-included__intro reveal">
            <p className="proposal-kicker">Included with the build</p>
            <h2 id="proposal-included-title">
              The website is only the front door. The support system comes
              with it.
            </h2>
            <p>
              The direct-order experience, its campaign engine and its support
              channel are presented as one practical owner package.
            </p>
          </div>

          <div className="proposal-included__ledger reveal">
            <article>
              <div>
                <span>01</span>
                <small>Core build</small>
              </div>
              <h3>Direct-order experience</h3>
              <p>
                The customer journey shown in this MVP, shaped around Sub
                Zero&apos;s products, bucket builder and proposed delivery
                days.
              </p>
            </article>
            <article>
              <div>
                <span>02</span>
                <small>Included</small>
              </div>
              <img
                className="proposal-included__partner-logo proposal-included__partner-logo--social"
                src="/partners/social-ai-studio-logo.webp"
                alt="Social AI Studio logo"
                width="954"
                height="396"
                loading="lazy"
                decoding="async"
              />
              <h3>Social AI Studio</h3>
              <p>
                Create Facebook and Instagram posts, campaign images and draft
                content calendars around new flavours, weekly drops and
                seasonal offers&mdash;using Sub Zero&apos;s brand profile as
                context.
              </p>
            </article>
            <article>
              <div>
                <span>03</span>
                <small>Included</small>
              </div>
              <img
                className="proposal-included__partner-logo proposal-included__partner-logo--bug"
                src="/partners/bug-squasher-icon.svg"
                alt="Bug Squasher support app icon"
                width="512"
                height="512"
                loading="lazy"
                decoding="async"
              />
              <h3>Bug Squasher</h3>
              <p>
                Give Sub Zero staff a personalised support app for reporting
                website issues with screenshots, photos or video. Penny Wise
                can triage, acknowledge and track each concern without
                Facebook-message ping-pong.
              </p>
            </article>
          </div>

          <p className="proposal-included__scope reveal">
            Included account setup and ongoing support terms are confirmed in
            the final scope. Any third-party usage or media costs are itemised
            before launch.
          </p>
        </section>

        <section className="proposal-choice">
          <p className="proposal-kicker reveal">Choose a view</p>
          <h2 className="reveal">See what customers feel. Then see how it could work.</h2>
          <div className="proposal-choice__cards reveal">
            <a href="/experience">
              <span>For the customer</span>
              <strong>Enter the bright shopper experience.</strong>
              <em>
                Open the MVP
                <ArrowRight aria-hidden="true" />
              </em>
            </a>
            <a href="/direct-delivery-case">
              <span>For the owner</span>
              <strong>Inspect the four-week direct-delivery case.</strong>
              <em>
                Open the proposal
                <ArrowRight aria-hidden="true" />
              </em>
            </a>
          </div>
        </section>

        <section
          className="proposal-next-step"
          id="proposal-next-step"
          aria-labelledby="proposal-next-step-title"
        >
          <div className="proposal-next-step__statement reveal">
            <p className="proposal-kicker">The sensible next step</p>
            <h2 id="proposal-next-step-title">
              Bring the real numbers.
              <br />
              Leave with a <em>pilot decision.</em>
            </h2>
          </div>
          <div className="proposal-next-step__close reveal">
            <p>
              Approve one 30-minute working session with Steve. Together, we
              can confirm the actual merchant fees, proposed service zones,
              possible run days, available capacity and the scope of a
              production build.
            </p>
            <div className="proposal-next-step__facts">
              <span>One working session</span>
              <span>Real inputs, not forecasts</span>
              <span>Proceed, adjust or stop</span>
            </div>
            <a
              className="proposal-next-step__action"
              href="mailto:?subject=Sub%20Zero%20Treats%20%E2%80%94%20next%20step&body=Steve%2C%20I%27d%20like%20to%20book%20a%2030-minute%20working%20session%20to%20review%20the%20direct-delivery%20proposal%2C%20confirm%20the%20real%20inputs%20and%20agree%20the%20next%20step."
            >
              Prepare the next-step reply
              <Mail aria-hidden="true" />
            </a>
            <small>
              This prepares a reply in your email app. Send it to Steve using
              the contact details that accompanied this private proposal. No
              pilot, order or subscription is activated here.
            </small>
          </div>
        </section>
      </main>

      <footer className="proposal-footer">
        <span>Penny Wise I.T × Sub Zero Treats</span>
        <p>Private clickable proposal · No order, payment or subscription is created.</p>
        <a href="#top">Back to top</a>
      </footer>
    </div>
  )
}

export default ProposalIntroPage
